import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button, Box } from "@mui/material";
import { grey } from "@mui/material/colors";
import { styled } from "@mui/material/styles";
import {
  setContentLayers,
  setLayerArray,
  setLayerMedias,
  setTextLayers,
  setTextOptionsOpen,
  updateContentLayerById,
} from "../../../../store/actions/cmsAction";
import {
  bplrtOptions,
  getPublicHtmlAsset,
} from "../../../../externalFeatures/components/bplrt";
import { openActionDialog } from "../../../global/ActionConfirmationDialog";
import { TypographyLarge } from "../../../customComponent";
import { logger } from "../../../../helpers/logger";
import FONTS from "../../../../fonts";
import update from "immutability-helper";
import Marquee from "react-fast-marquee";
import { Rnd } from "react-rnd";
import ContextMenu from "./ContextMenu";

export const CMenuButton = styled(Button)({
  fontSize: 14,
  fontWeight: 600,
  padding: "4px",
  minWidth: 50,
  height: 30,
  color: "black",
  cursor: "default",
  backgroundColor: grey[100],
  "&:hover": {
    backgroundColor: grey[300],
  },
  "&.disabled-btn": {
    color: grey[400],
  },
});

const PreviewItem = ({ layerID, index, layer, pauseViewer }) => {
  const dispatch = useDispatch();
  const {
    contentLayers,
    layerMedias,
    layerArray,
    textLayers,
    canvasRes,
    contentRes,
    widthRatio,
    heightRatio,
  } = useSelector((state) => state.cms);
  const { actionDialogOpen } = useSelector((state) => state.global);

  const { t } = useTranslation("cms");

  const [layerPreview, setLayerPreview] = useState(null);
  const [layerSize, setLayerSize] = useState({
    width: 0,
    height: 0,
    left: 0,
    top: 0,
  });

  const [oldFirstMedia, setOldFirstMedia] = useState(null);
  const layerGroupScheduler = useRef({});

  const [itemIndex, setItemIndex] = useState(0);
  const [itemChildrenIndex, setItemChildrenIndex] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);

  const [contextMenu, setContextMenu] = useState(null);
  const [contextMenuEnabled, setContextMenuEnabled] = useState(false);
  const [isLayerLocked, setIsLayerLocked] = useState(false);
  const contextMenuOpen = Boolean(contextMenu);

  const layerRef = useRef(null);
  const videoRef = useRef(null);
  const bplrtRef = useRef(null);

  const handleOpenActionDialog = (action, cancel) => {
    openActionDialog(dispatch, action, cancel);
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleRightClickMenu = (event) => {
    event.preventDefault();
    setBoxToActive(layerID);
    if (layer.type === "text") {
      dispatch(setTextOptionsOpen(true));
    }

    setContextMenu(
      !contextMenuOpen
        ? {
            mainX: event.clientX + 4,
            mainY: event.clientY + 2,
            textX: event.clientX - 44,
            textY: event.clientY - 77,
          }
        : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
          // Other native context menus might behave different.
          // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
          null
    );
  };

  const parseObjectFit = (assetFit) => {
    switch (assetFit) {
      default:
      case "FIT":
        return "contain";
      case "STRETCH":
        return "fill";
    }
  };

  const clearGroupedScheduler = () => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const schedulers = layerGroupScheduler.current;
    Object.keys(schedulers).forEach((s) => {
      clearInterval(schedulers[s]);
    });
  };

  useEffect(() => {
    return () => {
      videoRef.current = null;
      bplrtRef.current = null;
      clearGroupedScheduler();
    };
  }, []);

  useEffect(() => {
    const lastUpdate = { ...itemChildrenIndex };
    const assets = layerMedias[layerID];
    if (!assets) {
      return;
    }

    assets.forEach((asset) => {
      if (asset.children?.length > 0) {
        if (!lastUpdate[asset.id]) {
          lastUpdate[asset.id] = 0;
        }
      }
    });

    setItemChildrenIndex(lastUpdate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layerMedias[layerID]]);

  const parseLayerBorderWidth = useMemo(() => {
    if (isLayerLocked) {
      return 2;
    }
    return layer.active ? 2 : 1;
  }, [layer, isLayerLocked]);

  const parseLayerBorderColor = useMemo(() => {
    if (isLayerLocked) {
      return "red";
    }
    return layer.active ? "dodgerblue" : "unset";
  }, [layer, isLayerLocked]);

  const zoomValueHTML = useMemo(() => {
    return (canvasRes.width - 3) / contentRes.width;
  }, [canvasRes, contentRes]);

  useEffect(() => {
    const ratiodWidth = Math.round(layer.width / widthRatio);
    const ratiodHeight = Math.round(layer.height / heightRatio);

    if (layerMedias[layerID]?.length > 0) {
      const medias = [...layerMedias[layerID]];
      let previewEl = null;
      let fileSrc = null;

      let firstMedia = medias[itemIndex];
      if (!firstMedia) {
        firstMedia = medias[itemIndex - 1];
      }
      setOldFirstMedia(firstMedia.id);
      if (firstMedia.id !== oldFirstMedia) {
        logger.log(`firstMedia-AssetChanged!`);
      }

      switch (firstMedia.type) {
        case "VIDEO":
          fileSrc = firstMedia.vidSrc;
          previewEl = (
            <video
              ref={(ref) => {
                videoRef.current = ref;
                if (videoRef.current) {
                  if (pauseViewer) {
                    videoRef?.current?.pause();
                  } else {
                    videoRef?.current?.play();
                  }
                }
              }}
              draggable={false}
              alt={firstMedia.name}
              loop={true}
              muted={true}
              controls={false}
              src={firstMedia.url ?? fileSrc}
              onClick={(e) => e.preventDefault()}
              style={{
                height: "100%",
                width: "100%",
                objectFit: parseObjectFit(firstMedia.assetFit),
              }}
            />
          );
          break;

        case "IMAGE":
          fileSrc = firstMedia.url ?? firstMedia.src;
          previewEl = (
            <img
              draggable={false}
              src={fileSrc}
              alt={`${t("designer.previewItemAltTextPrefix")} #${index}`}
              style={{
                height: "100%",
                width: "100%",
                objectFit: parseObjectFit(firstMedia.assetFit),
              }}
            />
          );
          break;

        case "HTML":
          fileSrc = firstMedia.url ?? firstMedia.src;
          previewEl = (
            <iframe
              draggable={false}
              title={`HTML #${index}`}
              {...(firstMedia.url && {
                src: firstMedia.url,
              })}
              {...(firstMedia.html && {
                srcDoc: firstMedia.html,
              })}
              src={fileSrc}
              width={"100%"}
              height={"100%"}
              scrolling={"no"}
              style={{
                border: "none",
                overflow: "hidden",
                pointerEvents: "none",
                zoom: zoomValueHTML,
              }}
            />
          );
          break;

        case "BPLRT_EQUIPMENT":
          fileSrc = getPublicHtmlAsset(bplrtOptions[firstMedia.type].htmlSrc);
          previewEl = (
            <iframe
              draggable={false}
              title={`EQUIPMENT #${index}`}
              src={fileSrc}
              width={"100%"}
              height={"100%"}
              scrolling={"no"}
              style={{
                border: "none",
                overflow: "hidden",
                pointerEvents: "none",
                zoom: zoomValueHTML,
              }}
            />
          );
          break;

        case "BPLRT_EQUIPMENT_TABLE":
          fileSrc = getPublicHtmlAsset(bplrtOptions[firstMedia.type].htmlSrc);
          previewEl = (
            <iframe
              draggable={false}
              title={`EQUIPMENT_TABLE #${index}`}
              src={fileSrc}
              width={"100%"}
              height={"100%"}
              scrolling={"no"}
              style={{
                border: "none",
                overflow: "hidden",
                pointerEvents: "none",
                zoom: zoomValueHTML,
              }}
            />
          );
          break;

        case "BPLRT_SCHEDULE":
          fileSrc = getPublicHtmlAsset(bplrtOptions[firstMedia.type].htmlSrc);
          previewEl = (
            <iframe
              ref={(el) => {
                if (el) {
                  if (!bplrtRef.current) {
                    setTimeout(() => {
                      el.contentWindow.postMessage({
                        type: "LOADED",
                        payload: "BP1",
                      });
                      bplrtRef.current = el;
                    }, 777);
                  }
                }
              }}
              draggable={false}
              title={`SCHEDULE #${index}`}
              src={fileSrc}
              width={"100%"}
              height={"100%"}
              scrolling={"no"}
              style={{
                border: "none",
                overflow: "hidden",
                pointerEvents: "none",
                zoom: zoomValueHTML,
              }}
            />
          );
          break;

        case "BPLRT_SERVICE_INFO":
          fileSrc = getPublicHtmlAsset(bplrtOptions[firstMedia.type].htmlSrc);
          previewEl = (
            <iframe
              draggable={false}
              title={`SERVICE_INFO #${index}`}
              src={fileSrc}
              width={"100%"}
              height={"100%"}
              scrolling={"no"}
              style={{
                border: "none",
                overflow: "hidden",
                pointerEvents: "none",
                zoom: zoomValueHTML,
              }}
            />
          );
          break;

        case "BPLRT_SMP":
          fileSrc = getPublicHtmlAsset(bplrtOptions[firstMedia.type].htmlSrc);
          previewEl = (
            <iframe
              draggable={false}
              title={`SMP #${index}`}
              src={fileSrc}
              width={"100%"}
              height={"100%"}
              scrolling={"no"}
              style={{
                border: "none",
                overflow: "hidden",
                pointerEvents: "none",
                zoom: zoomValueHTML,
              }}
            />
          );
          break;

        case "GROUPED":
          //// WIP: Added after slideshow in Template Box implemented!
          // const childrenIndex = groupChildIndex[firstMedia.id] ?? 0;
          // const firstChildren = firstMedia.children[childrenIndex];

          const childrenIndex = itemChildrenIndex[firstMedia.id] ?? 0;
          let firstChildren = firstMedia.children[childrenIndex];
          if (!firstChildren) {
            firstChildren = firstMedia.children[childrenIndex - 1];
          }
          switch (firstChildren.type) {
            case "VIDEO":
              fileSrc = firstChildren.vidSrc;
              previewEl = (
                <video
                  ref={(ref) => {
                    videoRef.current = ref;
                    if (videoRef.current) {
                      if (pauseViewer) {
                        videoRef?.current?.pause();
                      } else {
                        videoRef?.current?.play();
                      }
                    }
                  }}
                  draggable={false}
                  alt={firstChildren.name}
                  loop={true}
                  muted={true}
                  controls={false}
                  src={firstChildren.url ?? fileSrc}
                  onClick={(e) => e.preventDefault()}
                  style={{
                    height: "100%",
                    width: "100%",
                    objectFit: parseObjectFit(firstMedia.assetFit),
                  }}
                />
              );
              break;

            default:
            case "IMAGE":
              fileSrc = firstChildren.url ?? firstChildren.src;
              previewEl = (
                <img
                  draggable={false}
                  src={fileSrc}
                  alt={`${t("designer.previewItemAltTextPrefix")} #${index}`}
                  style={{
                    height: "100%",
                    width: "100%",
                    objectFit: parseObjectFit(firstMedia.assetFit),
                  }}
                />
              );
              break;
          }

          // // WIP: Added after slideshow in Template Box implemented!
          // clearGroupedScheduler();
          // const schedulerDuration = firstMedia.interval * 1000;
          // layerGroupScheduler.current[firstMedia.id] = setInterval(() => {
          //   const updated = { ...groupChildIndex };
          //   let updatedIndex = childrenIndex + 1;
          //   if (updatedIndex >= firstMedia.children.length) {
          //     updatedIndex = 0;
          //   }

          //   updated[firstMedia.id] = updatedIndex;
          //   logger.log(`update-GroupChildIndex:`, updated[firstMedia.id]);
          //   setGroupChildIndex(updated);
          // }, schedulerDuration);
          break;

        default:
        case "VISTAR_ADVERTISEMENT":
        case "HIVESTACK_ADVERTISEMENT":
          previewEl = (
            <img
              draggable={false}
              src={`/ad_slot.png`}
              alt={`${t("designer.previewItemAltTextPrefix")} #${index}`}
              style={{
                height: "100%",
                width: "100%",
                objectFit: "fill",
              }}
            />
          );
          break;
      }

      setLayerPreview(previewEl);
    } else {
      setLayerPreview(null);
    }

    const updatedSize = {
      top: layer.top,
      left: layer.left,
      width: ratiodWidth,
      height: ratiodHeight,
    };
    setLayerSize(updatedSize);
    setContextMenuEnabled(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    layer,
    layerMedias,
    isResizing,
    isDragging,
    pauseViewer,
    widthRatio,
    heightRatio,
    canvasRes,
    itemIndex,
    itemChildrenIndex,
  ]);

  const textLayer = useMemo(() => {
    return layer.text;
  }, [layer]);

  const textSizeRatiod = useMemo(() => {
    if (!textLayer) {
      return 14;
    }

    let ratiodSize = Math.ceil(textLayer.size / widthRatio);
    if (ratiodSize <= 0) ratiodSize = 1;
    return ratiodSize;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layer, contentRes, canvasRes, widthRatio]);

  const parseAlignTextHorizontal = useMemo(() => {
    switch (textLayer?.hAlign) {
      default:
      case "LEFT":
        return "left";
      case "CENTER":
        return "center";
      case "RIGHT":
        return "right";
      case "JUSTIFY":
        return "justify";
    }
  }, [textLayer]);

  const parseAlignTextVertical = useMemo(() => {
    switch (textLayer?.vAlign) {
      case "TOP":
        return "baseline";
      default:
      case "CENTER":
        return "center";
      case "BOTTOM":
        return "end";
    }
  }, [textLayer]);

  const textComponent = useMemo(() => {
    if (!textLayer) return null;

    return (
      <TypographyLarge
        sx={{
          pl: 1,
          pr: textLayer?.scrolling ? 10 : 1,
          height: layerSize.height,
          textAlign: parseAlignTextHorizontal,
          alignContent: parseAlignTextVertical,
          overflow: "hidden",
          textWrap: "nowrap",
          textOverflow: "clip",
          fontSize: `${textSizeRatiod}px`,
          fontWeight: textLayer.weight,
          color: textLayer.color,
          display: "grid",
          fontFamily: FONTS[textLayer.family]?.value ?? "ARIAL",
        }}
      >
        {textLayer.text}
      </TypographyLarge>
    );
  }, [
    layerSize,
    parseAlignTextHorizontal,
    parseAlignTextVertical,
    textLayer,
    textSizeRatiod,
  ]);

  const previewElement = useMemo(() => {
    switch (layer.type) {
      default:
      case "media":
        if (layerPreview) return layerPreview;
        else
          return (
            <img
              draggable={false}
              src={`https://placehold.co/${layer.width}x${layer.height}`}
              alt={`${t("designer.previewItemAltTextPrefix")} #${index}`}
              style={{
                height: "100%",
                width: "100%",
                objectFit: "fill",
              }}
            />
          );

      case "text":
        if (textLayer?.scrolling) {
          return (
            <Marquee
              play={!pauseViewer}
              speed={textLayer?.speed * 40}
              direction={textLayer?.direction?.toLowerCase()}
            >
              {textComponent}
            </Marquee>
          );
        } else {
          return <Box sx={{ width: "100%" }}>{textComponent}</Box>;
        }

      case "ticker":
        return (
          <iframe
            draggable={false}
            title={`Ticker #${index}`}
            src={getPublicHtmlAsset("ticker.html")}
            width={"100%"}
            height={"100%"}
            scrolling={"no"}
            style={{
              border: "none",
              overflow: "hidden",
              pointerEvents: "none",
              zoom: zoomValueHTML, // TODO: custom zoom
            }}
          />
        );
    }
  }, [
    t,
    index,
    layer,
    layerPreview,
    pauseViewer,
    textComponent,
    textLayer,
    zoomValueHTML,
  ]);

  const setBoxToActive = useCallback(
    (id, layers) => {
      const targetLayers = layers ?? contentLayers;
      let mergedUpdates = { ...targetLayers };
      const layerKeys = Object.keys(mergedUpdates);
      for (let i = 0; i < layerKeys.length; i++) {
        const layerID = layerKeys[i];
        if (layerID === id) {
          mergedUpdates = update(mergedUpdates, {
            [layerID]: { $merge: { active: true } },
          });
        } else {
          mergedUpdates = update(mergedUpdates, {
            [layerID]: { $merge: { active: false } },
          });
        }
      }

      const finalUpdates = update(targetLayers, { $merge: mergedUpdates });
      if (layers) {
        return finalUpdates;
      } else {
        dispatch(setContentLayers({ ...finalUpdates }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contentLayers]
  );

  const moveBox = useCallback(
    (id, left, top) => {
      let mergedUpdates = contentLayers[id];
      if (left < 0 || top < 0) {
        return;
      }

      mergedUpdates = update(mergedUpdates, {
        dimensions: { $merge: { left, top } },
      });
      mergedUpdates = update(mergedUpdates, { $merge: { dragFlag: true } });

      dispatch(
        updateContentLayerById({
          contentLayers,
          layerID: id,
          update: mergedUpdates,
        })
      );
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contentLayers]
  );

  const resizeBox = useCallback(
    (id, delta, position) => {
      let adjustedPosX = position.x;
      let adjustedPosY = position.y;
      let adjustedWidth = layer.width + Math.round(delta.width * widthRatio);
      let adjustedHeight =
        layer.height + Math.round(delta.height * heightRatio);

      // Snap box to fit
      let paddingLimitWidth = contentRes.width;
      if (!(contentRes.free || contentRes.width > 2000)) {
        paddingLimitWidth = contentRes.width * 0.95;
      }
      let paddingLimitHeight = contentRes.height;
      if (!(contentRes.free || contentRes.height > 2000)) {
        paddingLimitHeight = contentRes.height * 0.95;
      }

      const originalAspectRatio = layer.width / layer.height;
      if (
        adjustedWidth > paddingLimitWidth &&
        adjustedWidth !== contentRes.width
      ) {
        adjustedWidth = contentRes.width;
        adjustedPosX = 0;
        if (layer.lockRatio) {
          adjustedHeight = Math.round(adjustedWidth / originalAspectRatio);
          if (adjustedHeight > paddingLimitHeight) {
            adjustedHeight = contentRes.height;
          }

          const combinedHeight = adjustedPosY * heightRatio + adjustedHeight;
          if (combinedHeight > contentRes.height) {
            adjustedPosY -= Math.round(
              (combinedHeight - contentRes.height) / heightRatio
            );
          }
        }
      } else if (
        adjustedHeight > paddingLimitHeight &&
        adjustedHeight !== contentRes.height
      ) {
        adjustedHeight = contentRes.height;
        adjustedPosY = 0;
        if (layer.lockRatio) {
          adjustedWidth = Math.round(adjustedHeight * originalAspectRatio);
          if (adjustedWidth > paddingLimitWidth) {
            adjustedWidth = contentRes.width;
          }

          const combinedWidth = adjustedPosX * widthRatio + adjustedWidth;
          if (combinedWidth > contentRes.width) {
            adjustedPosX -= Math.round(
              (combinedWidth - contentRes.width) / widthRatio
            );
          }
        }
      }

      let mergedUpdates = contentLayers[id];
      mergedUpdates = update(mergedUpdates, {
        dimensions: {
          $merge: {
            width: adjustedWidth,
            height: adjustedHeight,
            left: adjustedPosX,
            top: adjustedPosY,
          },
        },
      });
      mergedUpdates = update(mergedUpdates, { $merge: { dragFlag: true } });

      dispatch(
        updateContentLayerById({
          contentLayers,
          layerID: id,
          update: mergedUpdates,
        })
      );
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [layer, contentLayers, contentRes, canvasRes, widthRatio, heightRatio]
  );

  const handleOnDragStart = (e, d) => {
    setIsDragging(true);
    if (!layer.active) setBoxToActive(layerID);
    if (layer.type === "text") {
      dispatch(setTextOptionsOpen(true));
    }
  };

  const handleOnDragStop = (e, d) => {
    moveBox(layerID, d.x, d.y);
    setIsDragging(false);
  };

  const handleOnResizeStart = () => {
    setIsResizing(true);
    setBoxToActive(layerID);
  };

  const handleOnResizeStop = (delta, position) => {
    resizeBox(layerID, delta, position);
    setIsResizing(false);
  };

  const removeContainer = useCallback(
    (id, type) => {
      const updatedLayerArray = layerArray.filter((layerID) => layerID !== id);
      dispatch(setLayerArray(updatedLayerArray));

      const removedLayerZ = contentLayers[id].dimensions.zIndex;
      const layerKeys = Object.keys(contentLayers);
      let updatedContentLayers = { ...contentLayers };
      layerKeys.forEach((layerID) => {
        const layer = contentLayers[layerID];
        let adjustedDim = { ...layer.dimensions };
        if (removedLayerZ < adjustedDim.zIndex) {
          adjustedDim.zIndex -= 1;
          updatedContentLayers = update(updatedContentLayers, {
            [layerID]: {
              dimensions: {
                $merge: adjustedDim,
              },
            },
          });
        }
      });

      updatedContentLayers = update(updatedContentLayers, { $unset: [id] });
      dispatch(setContentLayers({ ...updatedContentLayers }));

      const updatedMedias = update(layerMedias, { $unset: [id] });
      dispatch(setLayerMedias({ forceUpdate: { ...updatedMedias } }));
      if (type === "text") {
        const updatedTextLayers = update(textLayers, { $unset: [id] });
        dispatch(setTextLayers({ ...updatedTextLayers }));
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contentLayers, layerMedias, textLayers]
  );

  const handleRemoveLayer = () => {
    setBoxToActive(layerID);
    handleOpenActionDialog(
      () => {
        removeContainer(layerID, layer.type);
      },
      () => {
        handleCloseContextMenu();
      }
    );
  };

  const parseLayerResizing = useMemo(() => {
    let value = !isLayerLocked ? true : false;
    return {
      bottom: value,
      bottomLeft: value,
      bottomRight: value,
      left: value,
      right: value,
      top: value,
      topLeft: value,
      topRight: value,
    };
  }, [isLayerLocked]);

  return (
    <>
      <ContextMenu
        open={contextMenuOpen}
        menu={contextMenu}
        layer={layer}
        layerID={layerID}
        layerItem={layerRef.current}
        handleClose={handleCloseContextMenu}
        handleRemoveLayer={handleRemoveLayer}
        itemIndex={itemIndex}
        setItemIndex={setItemIndex}
        shownAsset={layerMedias[layerID]?.[itemIndex] ?? {}}
        setItemChildrenIndex={setItemChildrenIndex}
        isLayerLocked={isLayerLocked}
        setIsLayerLocked={setIsLayerLocked}
      />
      <Rnd
        size={{ width: layerSize.width, height: layerSize.height }}
        position={{ x: layerSize.left, y: layerSize.top }}
        bounds={"parent"}
        lockAspectRatio={layer.lockRatio}
        disableDragging={isLayerLocked || contextMenuOpen || actionDialogOpen}
        enableResizing={parseLayerResizing}
        onDragStart={(e, d) => handleOnDragStart(e, d)}
        onDragStop={(e, d) => handleOnDragStop(e, d)}
        onResizeStart={() => handleOnResizeStart()}
        onResizeStop={(e, dir, ref, delta, position) =>
          handleOnResizeStop(delta, position)
        }
        onContextMenu={
          contextMenuEnabled && !actionDialogOpen
            ? handleRightClickMenu
            : undefined
        }
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          alignContent: "center",
          justifyContent: "center",
          borderStyle: "solid",
          borderWidth: parseLayerBorderWidth,
          borderColor: parseLayerBorderColor,
          backgroundColor:
            layer.bgColor?.length > 0 ? layer.bgColor : "lightgrey",
          zIndex: layer.zIndex ?? 0,
        }}
      >
        <Box ref={layerRef} width={"100%"} height={"100%"}>
          {previewElement}
        </Box>
      </Rnd>
    </>
  );
};

export default PreviewItem;
